/**
 * BOQ Unmatched Items Banner
 * Shows when material order items need to be manually matched to BOQ items.
 * Includes a dialog for resolving matches.
 */

import React, { useState, useEffect } from 'react';
import {
  Alert, AlertTitle, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, FormControl, InputLabel, Select, MenuItem, Divider, Chip
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import { brand, status as statusColors } from '../../theme/colors';

export default function BOQUnmatchedBanner({ boqId, unmatchedCount, boqItems = [], onLoadUnmatched, onLink, onRefresh }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [unmatchedItems, setUnmatchedItems] = useState([]);
  const [selections, setSelections] = useState({});
  const [loading, setLoading] = useState(false);

  const handleOpen = async () => {
    setDialogOpen(true);
    if (onLoadUnmatched) {
      const items = await onLoadUnmatched();
      setUnmatchedItems(items);
    }
  };

  const handleLink = async (materialOrderItemId) => {
    const boqItemId = selections[materialOrderItemId];
    if (!boqItemId || !onLink) return;

    setLoading(true);
    try {
      await onLink({ material_order_item_id: materialOrderItemId, boq_item_id: boqItemId });
      // Remove from list
      setUnmatchedItems(prev => prev.filter(i => i.material_order_item_id !== materialOrderItemId));
      if (onRefresh) onRefresh();
    } finally {
      setLoading(false);
    }
  };

  if (!unmatchedCount || unmatchedCount <= 0) return null;

  return (
    <>
      <Alert
        severity="warning"
        variant="outlined"
        sx={{ mb: 3, borderRadius: 2 }}
        action={
          <Button
            size="small"
            startIcon={<LinkIcon />}
            onClick={handleOpen}
            sx={{ color: statusColors.warningDark, fontWeight: 600 }}
          >
            Review
          </Button>
        }
      >
        <AlertTitle sx={{ fontWeight: 700 }}>
          {unmatchedCount} delivered material{unmatchedCount > 1 ? 's' : ''} need matching
        </AlertTitle>
        Materials from merchant orders were delivered but couldn't be automatically matched to your schedule.
        Link them manually so your budget tracking stays accurate.
      </Alert>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkIcon sx={{ color: brand.primary }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Match Delivered Materials</Typography>
        </DialogTitle>

        <DialogContent dividers>
          {unmatchedItems.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              All items have been matched. You're all set.
            </Typography>
          ) : (
            unmatchedItems.map((item, idx) => (
              <Box key={item.id}>
                {idx > 0 && <Divider sx={{ my: 2 }} />}
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Delivered Item:
                  </Typography>
                  <Chip
                    label={`Order #${(item.material_order_id || '').slice(-8)}`}
                    size="small"
                    sx={{ mb: 1, bgcolor: statusColors.infoBg, color: statusColors.info, fontWeight: 600, fontSize: '0.7rem' }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {item.boq_item_name || 'Unknown item'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Suggested match confidence: {Math.round((item.match_confidence || 0) * 100)}%
                  </Typography>
                </Box>

                <FormControl fullWidth size="small">
                  <InputLabel>Match to BOQ item</InputLabel>
                  <Select
                    value={selections[item.material_order_item_id] || ''}
                    label="Match to BOQ item"
                    onChange={(e) => setSelections(prev => ({ ...prev, [item.material_order_item_id]: e.target.value }))}
                  >
                    {boqItems.map(bi => (
                      <MenuItem key={bi.id} value={bi.id}>
                        {bi.item_name} ({bi.quantity} {bi.unit})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handleLink(item.material_order_item_id)}
                  disabled={!selections[item.material_order_item_id] || loading}
                  sx={{ mt: 1, bgcolor: brand.primary, '&:hover': { bgcolor: brand.primaryDark }, borderRadius: 1.5 }}
                >
                  Link
                </Button>
              </Box>
            ))
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
